import { useGetEnums } from 'api-hooks/common';
import { PreferenceGroupSubTypeEnum } from 'api-hooks/preference-tag';
import { Value } from 'baseui/select';
import { EnumList } from 'common/constants/enum';
import LoadingComponent from 'components/common/loading';
import { useFormState } from 'components/elements/form';
import deleteColumn from 'components/elements/table/editable-columns/delete-column';
import preferenceColumn from 'components/elements/table/editable-columns/preference-column';
import TableInput from 'components/elements/table/table-input';
import {
  ActionType,
  ColumnDataType,
  IColumn,
} from 'components/elements/table/type';
import useGetAirlineColumn from 'hooks/use-get-airline-column';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { addNewAirlineItem } from '../../hooks/use-get-default-values';
import {
  TicketSaleFlightAirlineFormType,
  TicketSaleFormType,
} from '../form-type';

interface Props {
  parentIndex: number;
}

interface UseScheduleAirlineTableListReturnType {
  columns: IColumn<TicketSaleFlightAirlineFormType>[];
  isFetching: boolean;
}

function useScheduleAirlineTableList(): UseScheduleAirlineTableListReturnType {
  const { t } = useTranslation();
  const { editable } = useFormState();
  const { data, isFetching } = useGetEnums({
    class: EnumList.FlightCheckInStatus,
  });
  const { getValues } = useFormContext<TicketSaleFormType>();
  const statusOptions = (data?.data || []).map((value) => {
    return {
      id: value.value,
      label: value.label,
    };
  });
  const airlineColumn = useGetAirlineColumn();
  const columns = [
    ...(getValues('ticketSale')
      ? [
          {
            header: t('common:status'),
            accessorKey: 'checkInStatus',
            minSize: 150,
            maxSize: 180,
            dataType: ColumnDataType.select,
            options: statusOptions,
          },
        ]
      : []),
    {
      header: t('common:airline'),
      accessorKey: 'airlineId',
      minSize: 100,
      dataType: ColumnDataType.select,
      selectProps: airlineColumn,
      required: true,
    },
    {
      header: t('common:date'),
      accessorKey: 'date',
      minSize: 100,
      dataType: ColumnDataType.date,
      required: true,
    },
    {
      header: t('common:detail'),
      accessorKey: 'detail',
      minSize: 100,
      dataType: ColumnDataType.text,
      required: true,
    },
    {
      header: t('common:class'),
      accessorKey: 'preferenceId',
      minSize: 200,
      dataType: ColumnDataType.select,
      selectProps: preferenceColumn({
        subType: PreferenceGroupSubTypeEnum.FlightClass,
      }),
    },
    {
      header: t('common:route'),
      accessorKey: 'route',
      minSize: 100,
      dataType: ColumnDataType.text,
      required: true,
    },
    {
      header: t('common:time'),
      accessorKey: 'time',
      minSize: 100,
      dataType: ColumnDataType.text,
      required: true,
    },
    {
      header: t('common:price'),
      accessorKey: 'price',
      minSize: 100,
      dataType: ColumnDataType.number,
      minNumber: 0,
      required: true,
    },
    ...deleteColumn(editable),
  ];
  return { columns, isFetching };
}

export default function ScheduleAirlineTable(props: Props) {
  const { editable } = useFormState();
  const name = `flightSales.${props.parentIndex}.flightSaleAirlines` as const;

  const { clearErrors, trigger, getValues, control } =
    useFormContext<TicketSaleFormType>();

  const { columns, isFetching } = useScheduleAirlineTableList();

  const { fields, update, remove, append } = useFieldArray({
    control,
    name,
    keyName: '__customId',
  });

  const dispatch = React.useCallback(
    (action) => {
      const airlines = getValues(name);
      const currentRowIndex = action.rowIndex as number;
      const airline = airlines[currentRowIndex];
      const type = action.type as ActionType;
      const columnId = action.columnId as keyof TicketSaleFlightAirlineFormType;
      const value = action.value;
      const extra = action.extra as Value;

      const otherVal: () => Partial<TicketSaleFlightAirlineFormType> = () => {
        if (columnId === 'airlineId') {
          return {
            airlineName: extra?.[0]?.label as string,
          };
        }
        return {};
      };

      if (type === 'addRow') {
        append(addNewAirlineItem());
        trigger(name);
      } else if (type === 'removeRow') {
        remove(currentRowIndex);
        trigger(name);
      } else if (type === 'update_cell') {
        update(currentRowIndex, {
          ...airline,
          [columnId]: value,
          ...otherVal(),
        });
        clearErrors(`${name}.${currentRowIndex}.${columnId}`);
      }
    },
    [append, clearErrors, getValues, name, remove, trigger, update],
  );

  if (isFetching) {
    return <LoadingComponent />;
  }

  return (
    <div style={{ width: '100%' }}>
      <TableInput
        columns={columns}
        data={fields}
        dispatch={dispatch}
        disabled={!editable}
        name={name}
        uniqueRowKey="customId"
      />
    </div>
  );
}
